<template>
  <div class="min-flow-chart">
    <div ref="chartContainer" class="chart-container"></div>
  </div>
</template>

<script>
export default {
  name: 'MinFlowChart',
  props: {
    data: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      chart: null,
      chartData: []
    }
  },
  watch: {
    data: {
      handler(newData) {
        this.chartData = [...newData]
        this.updateChart()
      },
      deep: true
    }
  },
  mounted() {
    this.chartData = [...this.data]
    this.initChart()
    this.startDataUpdate()
  },
  beforeDestroy() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
    }
    if (this.chart) {
      this.chart.dispose()
    }
  },
  methods: {
    initChart() {
      // 使用 Canvas 实现简单的折线图
      const container = this.$refs.chartContainer
      const canvas = document.createElement('canvas')
      
      canvas.width = container.offsetWidth
      canvas.height = container.offsetHeight
      canvas.style.width = '100%'
      canvas.style.height = '100%'
      
      container.appendChild(canvas)
      this.canvas = canvas
      this.ctx = canvas.getContext('2d')
      
      this.drawChart()
    },
    
    drawChart() {
      const ctx = this.ctx
      const width = this.canvas.width
      const height = this.canvas.height
      
      // 清空画布
      ctx.clearRect(0, 0, width, height)
      
      if (!this.chartData || this.chartData.length === 0) return
      
      const padding = { top: 20, right: 30, bottom: 30, left: 40 }
      const chartWidth = width - padding.left - padding.right
      const chartHeight = height - padding.top - padding.bottom
      
      // 获取数据范围
      const values = this.chartData.map(d => d.value)
      const maxValue = Math.max(...values)
      const minValue = Math.min(...values)
      const valueRange = maxValue - minValue || 1
      
      // 绘制网格线
      this.drawGrid(ctx, padding, chartWidth, chartHeight, maxValue, minValue)
      
      // 绘制坐标轴
      this.drawAxes(ctx, padding, chartWidth, chartHeight)
      
      // 绘制数据线
      this.drawLine(ctx, padding, chartWidth, chartHeight, maxValue, minValue)
      
      // 绘制数据点
      this.drawPoints(ctx, padding, chartWidth, chartHeight, maxValue, minValue)
      
      // 绘制标签
      this.drawLabels(ctx, padding, chartWidth, chartHeight)
    },
    
    drawGrid(ctx, padding, chartWidth, chartHeight, maxValue, minValue) {
      ctx.strokeStyle = 'rgba(0, 255, 200, 0.1)'
      ctx.lineWidth = 0.5
      
      // 水平网格线
      const horizontalLines = 5
      for (let i = 0; i <= horizontalLines; i++) {
        const y = padding.top + (chartHeight / horizontalLines) * i
        ctx.beginPath()
        ctx.moveTo(padding.left, y)
        ctx.lineTo(padding.left + chartWidth, y)
        ctx.stroke()
      }
      
      // 垂直网格线
      const verticalLines = this.chartData.length - 1
      for (let i = 0; i <= verticalLines; i++) {
        const x = padding.left + (chartWidth / verticalLines) * i
        ctx.beginPath()
        ctx.moveTo(x, padding.top)
        ctx.lineTo(x, padding.top + chartHeight)
        ctx.stroke()
      }
    },
    
    drawAxes(ctx, padding, chartWidth, chartHeight) {
      ctx.strokeStyle = 'rgba(0, 255, 200, 0.5)'
      ctx.lineWidth = 1
      
      // Y轴
      ctx.beginPath()
      ctx.moveTo(padding.left, padding.top)
      ctx.lineTo(padding.left, padding.top + chartHeight)
      ctx.stroke()
      
      // X轴
      ctx.beginPath()
      ctx.moveTo(padding.left, padding.top + chartHeight)
      ctx.lineTo(padding.left + chartWidth, padding.top + chartHeight)
      ctx.stroke()
    },
    
    drawLine(ctx, padding, chartWidth, chartHeight, maxValue, minValue) {
      if (this.chartData.length < 2) return
      
      ctx.strokeStyle = 'rgba(0, 255, 200, 0.8)'
      ctx.lineWidth = 2
      ctx.beginPath()
      
      this.chartData.forEach((item, index) => {
        const x = padding.left + (chartWidth / (this.chartData.length - 1)) * index
        const y = padding.top + chartHeight - ((item.value - minValue) / (maxValue - minValue)) * chartHeight
        
        if (index === 0) {
          ctx.moveTo(x, y)
        } else {
          ctx.lineTo(x, y)
        }
      })
      
      ctx.stroke()
      
      // 添加渐变填充
      const gradient = ctx.createLinearGradient(0, padding.top, 0, padding.top + chartHeight)
      gradient.addColorStop(0, 'rgba(0, 255, 200, 0.3)')
      gradient.addColorStop(1, 'rgba(0, 255, 200, 0.05)')
      
      ctx.beginPath()
      this.chartData.forEach((item, index) => {
        const x = padding.left + (chartWidth / (this.chartData.length - 1)) * index
        const y = padding.top + chartHeight - ((item.value - minValue) / (maxValue - minValue)) * chartHeight
        
        if (index === 0) {
          ctx.moveTo(x, y)
        } else {
          ctx.lineTo(x, y)
        }
      })
      
      ctx.lineTo(padding.left + chartWidth, padding.top + chartHeight)
      ctx.lineTo(padding.left, padding.top + chartHeight)
      ctx.closePath()
      ctx.fillStyle = gradient
      ctx.fill()
    },
    
    drawPoints(ctx, padding, chartWidth, chartHeight, maxValue, minValue) {
      this.chartData.forEach((item, index) => {
        const x = padding.left + (chartWidth / (this.chartData.length - 1)) * index
        const y = padding.top + chartHeight - ((item.value - minValue) / (maxValue - minValue)) * chartHeight
        
        // 外圈光晕
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, 6)
        gradient.addColorStop(0, 'rgba(0, 255, 200, 0.9)')
        gradient.addColorStop(1, 'rgba(0, 255, 200, 0.3)')
        
        ctx.beginPath()
        ctx.arc(x, y, 6, 0, Math.PI * 2)
        ctx.fillStyle = gradient
        ctx.fill()
        
        // 内圈
        ctx.beginPath()
        ctx.arc(x, y, 3, 0, Math.PI * 2)
        ctx.fillStyle = '#00ffc8'
        ctx.fill()
      })
    },
    
    drawLabels(ctx, padding, chartWidth, chartHeight) {
      const values = this.chartData.map(d => d.value)
      const maxValue = Math.max(...values)
      const minValue = Math.min(...values)
      
      ctx.fillStyle = '#cccccc'
      ctx.font = '10px Arial'
      
      // Y轴标签
      const horizontalLines = 5
      for (let i = 0; i <= horizontalLines; i++) {
        const value = minValue + (maxValue - minValue) * (1 - i / horizontalLines)
        const y = padding.top + (chartHeight / horizontalLines) * i
        ctx.textAlign = 'right'
        ctx.fillText(Math.round(value), padding.left - 5, y + 3)
      }
      
      // X轴标签
      this.chartData.forEach((item, index) => {
        const x = padding.left + (chartWidth / (this.chartData.length - 1)) * index
        ctx.textAlign = 'center'
        ctx.fillText(item.day, x, padding.top + chartHeight + 15)
      })
      
      // 标题
      ctx.fillStyle = '#00ffc8'
      ctx.font = '12px Arial'
      ctx.textAlign = 'center'
      ctx.fillText(this.$t('夜间最小流量趋势 (m³/h)'), padding.left + chartWidth / 2, 15)
    },
    
    updateChart() {
      this.drawChart()
    },
    
    startDataUpdate() {
      // 模拟数据更新
      this.updateInterval = setInterval(() => {
        this.chartData = this.chartData.map(item => ({
          ...item,
          value: Math.max(500, Math.min(2000, item.value + (Math.random() - 0.5) * 100))
        }))
        this.drawChart()
      }, 5000)
    }
  }
}
</script>

<style scoped>
.min-flow-chart {
  width: 100%;
  height: 100%;
}

.chart-container {
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  overflow: hidden;
}
</style>